import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useExpenseDisputes,
  useWithdrawDispute,
  useRejectDispute,
  useResolveDispute,
  useReopenDispute,
} from '@/hooks/useDisputes';
import { UserAvatar } from '@/components/common/UserAvatar';
import { RaiseDisputeDialog } from '@/components/disputes/RaiseDisputeDialog';
import { RESOLUTION_REASON_TYPES } from '@sharecost/shared';
import type { DisputeStatus, ResolutionReasonType } from '@sharecost/shared';
import type { DisputeWithUser } from '@/api/disputes';
import {
  TriangleAlert,
  Loader2,
  Check,
  X,
  Undo2,
  Edit,
  RefreshCcw,
} from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface DisputeSectionProps {
  expenseId: string;
  groupId: string;
  paidBy: string;
  expenseDescription: string;
  isUserParticipant: boolean;
  onEditExpense?: () => void;
}

const STATUS_STYLES: Record<DisputeStatus, string> = {
  open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  withdrawn: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

function formatTime(dateStr: string): string {
  try {
    return formatDistanceToNowStrict(new Date(dateStr), { addSuffix: true });
  } catch {
    return '';
  }
}

export function DisputeSection({
  expenseId,
  groupId,
  paidBy,
  expenseDescription,
  isUserParticipant,
  onEditExpense,
}: DisputeSectionProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: disputes = [], isLoading } = useExpenseDisputes(expenseId);
  const withdrawDispute = useWithdrawDispute();
  const rejectDispute = useRejectDispute();
  const resolveDispute = useResolveDispute();
  const reopenDispute = useReopenDispute();

  const [showRaiseDialog, setShowRaiseDialog] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReasonType, setRejectReasonType] = useState<ResolutionReasonType | ''>('');
  const [rejectNote, setRejectNote] = useState('');

  const openRejectForm = (id: string) => {
    setRejectingId(id);
    setRejectReasonType('');
    setRejectNote('');
  };

  const isPayer = user?.id === paidBy;
  const hasOwnOpenDispute = disputes.some(
    d => d.raised_by === user?.id && d.status === 'open',
  );
  const canRaise = !!user && !isPayer && isUserParticipant && !hasOwnOpenDispute;

  const openDisputes = disputes.filter(d => d.status === 'open');

  // Hide section entirely if no disputes and user can't raise one
  if (!isLoading && disputes.length === 0 && !canRaise) return null;

  const handleWithdraw = async (dispute: DisputeWithUser) => {
    if (!confirm(t('expenses:disputeResolve.withdrawDisputeConfirm'))) return;
    await withdrawDispute.mutateAsync({
      disputeId: dispute.id,
      expenseId,
      groupId,
    });
  };

  const handleResolve = async (dispute: DisputeWithUser) => {
    if (!user) return;
    await resolveDispute.mutateAsync({
      disputeId: dispute.id,
      expenseId,
      groupId,
      userId: user.id,
    });
  };

  const handleReject = async (disputeId: string) => {
    if (!user) return;
    await rejectDispute.mutateAsync({
      disputeId,
      expenseId,
      groupId,
      userId: user.id,
      reasonType: rejectReasonType || undefined,
      note: rejectNote.trim() || undefined,
    });
    setRejectingId(null);
    setRejectReasonType('');
    setRejectNote('');
  };

  const handleReopen = async (dispute: DisputeWithUser) => {
    await reopenDispute.mutateAsync({
      disputeId: dispute.id,
      expenseId,
      groupId,
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-quaternary">
          <TriangleAlert className="h-3.5 w-3.5" />
          {t('expenses:disputeSection.disputes')}
          {openDisputes.length > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-bold text-red-700 dark:text-red-400">
              {t('web:disputeSection.openCount', { count: openDisputes.length })}
            </span>
          )}
        </h3>
        {canRaise && (
          <button
            onClick={() => setShowRaiseDialog(true)}
            className="rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          >
            {t('expenses:dispute.raisedDispute')}
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
        </div>
      ) : disputes.length === 0 ? (
        canRaise ? (
          <p className="py-3 text-center text-sm text-text-quaternary">
            {t('web:disputeSection.somethingWrongWithThisExpenseUse')}
          </p>
        ) : null
      ) : (
        <div className="space-y-3">
          {disputes.map(dispute => {
            const isRaiser = dispute.raised_by === user?.id;
            const isOpen = dispute.status === 'open';
            const isRejected = dispute.status === 'rejected';
            const canReopen = isRaiser && isRejected && dispute.reopen_count < 1;
            const isRejecting = rejectingId === dispute.id;

            return (
              <div
                key={dispute.id}
                className="rounded-lg border border-border-secondary p-4"
              >
                {/* Dispute header */}
                <div className="flex items-start gap-2.5">
                  <UserAvatar
                    name={dispute.raised_by_user?.display_name}
                    avatarUrl={dispute.raised_by_user?.avatar_url}
                    size="sm"
                    className="mt-0.5 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-text-primary truncate">
                        {isRaiser ? t('common:you') : dispute.raised_by_user?.display_name ?? t('web:common.unknownUser')}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[dispute.status]}`}
                      >
                        {t(`expenses:dispute.status.${dispute.status}`)}
                      </span>
                      <span className="text-xs text-text-quaternary shrink-0">
                        {formatTime(dispute.created_at)}
                      </span>
                    </div>

                    {/* Reason */}
                    <p className="text-sm text-text-secondary">
                      <span className="font-medium">{t(`expenses:dispute.reason.${dispute.reason_type}`)}</span>
                      {dispute.reason_text && (
                        <span className="text-text-tertiary"> &mdash; {dispute.reason_text}</span>
                      )}
                    </p>

                    {/* Resolution details */}
                    {dispute.resolved_by_user && dispute.resolved_at && (
                      <div className="mt-2 rounded-md bg-surface-secondary px-3 py-2 text-xs text-text-tertiary">
                        <span className="font-medium text-text-secondary">
                          {dispute.resolved_by === user?.id
                            ? t('common:you')
                            : dispute.resolved_by_user.display_name}
                        </span>
                        {' '}{dispute.status === 'resolved' ? t('web:disputeSection.resolvedThis') : t('web:disputeSection.rejectedThis')}{' '}
                        {formatTime(dispute.resolved_at)}
                        {dispute.resolution_reason_type && (
                          <> &mdash; {t(`expenses:dispute.resolution.${dispute.resolution_reason_type}`)}</>
                        )}
                        {dispute.resolution_note && (
                          <p className="mt-1 text-text-tertiary italic">"{dispute.resolution_note}"</p>
                        )}
                      </div>
                    )}

                    {/* Reject form (inline) */}
                    {isRejecting && (
                      <div className="mt-3 space-y-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-3">
                        <label className="block text-xs font-medium text-text-secondary">
                          {t('web:disputeSection.rejectionReason')} <span className="text-text-quaternary">{t('web:common.optional')}</span>
                        </label>
                        <select
                          value={rejectReasonType}
                          onChange={e => setRejectReasonType(e.target.value as ResolutionReasonType | '')}
                          className="w-full rounded-md border border-border-secondary px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        >
                          <option value="">{t('web:disputeSection.selectAReason')}</option>
                          {RESOLUTION_REASON_TYPES.map(type => (
                            <option key={type} value={type}>{t(`expenses:dispute.resolution.${type}`)}</option>
                          ))}
                        </select>
                        <textarea
                          value={rejectNote}
                          onChange={e => setRejectNote(e.target.value)}
                          placeholder={t('web:disputeSection.addANote')}
                          maxLength={1000}
                          rows={2}
                          className="w-full rounded-md border border-border-secondary px-2 py-1.5 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReject(dispute.id)}
                            disabled={rejectDispute.isPending}
                            className="flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {rejectDispute.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                            {t('expenses:disputeResolve.reject')}
                          </button>
                          <button
                            onClick={() => { setRejectingId(null); setRejectReasonType(''); setRejectNote(''); }}
                            className="rounded-md px-3 py-1.5 text-xs text-text-tertiary hover:bg-surface-hover"
                          >
                            {t('common:button.cancel')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    {!isRejecting && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {/* Payer actions (resolve, edit & resolve, reject) */}
                        {isPayer && isOpen && (
                          <>
                            <button
                              onClick={() => handleResolve(dispute)}
                              disabled={resolveDispute.isPending}
                              className="flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                              {resolveDispute.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Check className="h-3 w-3" />
                              )}
                              {t('expenses:disputeResolve.resolve')}
                            </button>
                            {onEditExpense && (
                              <button
                                onClick={onEditExpense}
                                className="flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-700 transition-colors"
                              >
                                <Edit className="h-3 w-3" />
                                {t('expenses:disputeResolve.editResolve')}
                              </button>
                            )}
                            <button
                              onClick={() => openRejectForm(dispute.id)}
                              className="flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 px-2.5 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              <X className="h-3 w-3" />
                              {t('expenses:disputeResolve.reject')}
                            </button>
                          </>
                        )}

                        {/* Raiser actions (withdraw for open, reopen for rejected) */}
                        {isRaiser && isOpen && (
                          <button
                            onClick={() => handleWithdraw(dispute)}
                            disabled={withdrawDispute.isPending}
                            className="flex items-center gap-1 rounded-md border border-border-secondary px-2.5 py-1.5 text-xs font-medium text-text-tertiary hover:bg-surface-hover disabled:opacity-50 transition-colors"
                          >
                            {withdrawDispute.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Undo2 className="h-3 w-3" />
                            )}
                            {t('expenses:disputeResolve.withdraw')}
                          </button>
                        )}

                        {canReopen && (
                          <button
                            onClick={() => handleReopen(dispute)}
                            disabled={reopenDispute.isPending}
                            className="flex items-center gap-1 rounded-md border border-yellow-300 dark:border-yellow-700 px-2.5 py-1.5 text-xs font-medium text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 disabled:opacity-50 transition-colors"
                          >
                            {reopenDispute.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCcw className="h-3 w-3" />
                            )}
                            {t('expenses:disputeResolve.reopen')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Raise dispute dialog */}
      <RaiseDisputeDialog
        open={showRaiseDialog}
        onClose={() => setShowRaiseDialog(false)}
        expenseId={expenseId}
        groupId={groupId}
        expenseDescription={expenseDescription}
      />
    </div>
  );
}
